import { GeoJsonProperties, Position } from "geojson";

export interface Tool {
  id: number;
  name: string;
  type: string;
  ngedId?: string;
}

export interface NetworkAsset {
  id: string;
  type: string;
  markerGeometry?: LatLngObj;
  polyGeometry?: (LatLngObj | LatLngObj[])[];
  name?: string;
  ngedId?: string;
  topographyId?: string;
  firstConnectedAsset?: string;
  secondConnectedAsset?: string;
  isConnected: boolean;
  pointOfConnectionType?: string;
  isConsumer: boolean;
  cableRag?: string;
  cableType?: "Mains" | "Service";
  cableType2?: string;
  cableTypeId?: number;
  editModeFirstConnectedAsset?: string;
  editModeSecondConnectedAsset?: string;
}

export type KeyValuePair = { [index: string]: string | number };

export interface ExistingNetworkAsset<T = unknown> extends NetworkAsset {
  properties?: T & { color?: string };
}

export interface LatLngObj {
  lat: number;
  lng: number;
}

export interface Premise {
  id: string;
  label: string;
  name: string;
  properties: string[];
}

export type ConnectionPoint = {
  id: string;
  type: string;
  phases: string;
  quantity: string;
  diversifiedQuantity: string;
  undiversifiedQuantity: string;
  unit: string;
  subConnectionPoints: SubConnectionPoint[];
};

export type SubConnectionPoint = {
  id: number;
  type: string;
  quantity: string;
  unit: string;
};

export type DiversifiableSubConnectionPoint = SubConnectionPoint & {
  diversifiedQuantity: string;
  undiversifiedQuantity: string;
};

export type CapacitySubConnectionPoint = SubConnectionPoint & {
  type: "battery";
  importCapacity: string;
  exportCapacity: string;
};

export interface FeatureData {
  coords: Position[];
  properties: GeoJsonProperties;
  groundType: string | null;
  length: number;
  pointOfConnection: boolean;
}

export interface LVAceRequest {
  totalkVA: number;
  maxConsumerPhase: number;
  isInPublic: boolean;
  study: NetworkAsset[];
}

export interface UserRole {
  [key: string]: string;
}

export interface EditableJoint {
  latlng: LatLngObj;
  assets: TransposedAsset[];
}

export interface IntersectingSegment {
  id: string;
  data: FeatureData[];
}

export interface TransposedAsset {
  id: string;
  coord?: LatLngObj;
  asset: NetworkAsset;
  index: number;
  polyIndex?: number;
}

export interface SharedPoints {
  id: string;
  coord: LatLngObj;
  arr: TransposedAsset[];
}

export interface GeoServerCable {
  Id: number;
  AcLineSegmentId: string;
  Class: string;
  DiagramId: number;
  SubstationNumber: number;
  FeederNumber: number;
  RatingType: string;
  Annotation: string | null;
  NumberOfPhases: string;
  NumberOfPhasesIsDefault: boolean;
  Rating: number;
  RatingIsDefault: boolean;
  CableType: string;
  CableTypeIsDefault: boolean;
  AutoSelect: boolean;
  Length: number;
  OverrideLength: number;
  StartAssetId: string;
  EndAssetId: string;
  CableGroup: string;
  CableGroupIsDefault: boolean;
  Ducting: boolean;
  GroupedConnectionPoints: string;
  MaxCurrentAmps: number | null;
  MaxUtilisationPercent: number | null;
  MaximumBranchUtilisationUpstreamPercent: number | null;
  MaximumBranchUtilisationUpstreamRatingAmps: number | null;
  MinimumBranchCapacityUpstreamKVA: number | null;
  MinimumBranchCapacityUpstreamRatingAmps: number | null;
  Season: string | null;
  SourceLoopOperatingImpedance: number | null;
  MaxVoltDropPercent: number | null;
  MaxVoltRisePercent: number | null;
  TransformerMaxUtilisationPercent: number | null;
  TotalAtcEnergyConsumption: number;
  CableRatingAmps: number | null;
  TransformerRatingkVA: number | null;
  SystemVoltage: number | null;
  color: string;
}

export interface DrawableLine {
  id: string;
  positions: Position[];
  type: string;
  arrowheadSize?: number;
}

export interface DrawableShape {
  id: string;
  positions: Position[] | Position[][];
  type: string;
  color: string;
}

export interface DrawableAnnotation {
  id: string;
  text: string;
  lexical: string;
  fontSize: number;
  color: string;
  borderColor: string;
  textColor: string;
  position: Position;
  width: number;
}

export interface Message {
  code: string;
  description: string;
  level: number | null;
  link: string;
}

export type Schematic = {
  id: string;
  image: string;
  fileName: string;
  opacity: number;
  uniformScaling: boolean;
  visible: boolean;
  positions: number[][];
};

export enum AssetIdPrefix {
  cable = "AcLineSegment",
  pole = "JunctionIsPole",
  transformer = "PowerTransformer",
}
